/**
 * @module services/health-conversation-template
 * @description CRUD de plantillas de HealthConversation. Edición restringida
 *              por permission `whatsappConfig.edit`. Las 4 plantillas seed
 *              (`post_surgery`, `mental_health_weekly`, `adherence`,
 *              `oncology_post_chemo`) NO se borran — solo toggle activa.
 */
import api from "./api";

const healthConversationTemplateService = {
  /**
   * @description Lista todas las plantillas de la org (activas + inactivas).
   * @param {Object} [params] - { active: 'true'|'false' } filtro opcional
   * @returns {Promise<Array>} array de plantillas
   */
  list(params = {}) {
    return api.get("/health-conversation-templates", { params });
  },

  /**
   * @description Detalle de 1 plantilla.
   * @param {string} id
   * @returns {Promise<Object>}
   */
  get(id) {
    return api.get(`/health-conversation-templates/${id}`);
  },

  /**
   * @description Crea plantilla custom (no seed). `key` no puede ser una
   *              de las 4 reservadas.
   * @param {Object} payload
   * @returns {Promise<Object>}
   */
  create(payload) {
    return api.post("/health-conversation-templates", payload);
  },

  /**
   * @description Edita plantilla. Para seed solo pisa campos editables.
   * @param {string} id
   * @param {Object} payload
   * @returns {Promise<void>}
   */
  update(id, payload) {
    return api.put(`/health-conversation-templates/${id}`, payload);
  },

  /**
   * @description Borra plantilla. Bloqueado para seed (devuelve 400).
   * @param {string} id
   * @returns {Promise<void>}
   */
  remove(id) {
    return api.delete(`/health-conversation-templates/${id}`);
  },
};

/**
 * @description Modelos LLM permitidos para conductor/evaluador. Mantener
 *              sincronizado con `ALLOWED_MODELS` del schema en la API.
 */
export const ALLOWED_MODELS = [
  {
    value: "claude-haiku-4-5",
    label: "Claude Haiku 4.5",
    costPerCallUsd: 0.0001,
  },
  {
    value: "claude-sonnet-4-5",
    label: "Claude Sonnet 4.5",
    costPerCallUsd: 0.0015,
  },
  { value: "claude-opus-4-5", label: "Claude Opus 4.5", costPerCallUsd: 0.008 },
  { value: "gpt-4o-mini", label: "GPT-4o mini", costPerCallUsd: 0.0001 },
  { value: "gpt-4o", label: "GPT-4o", costPerCallUsd: 0.0025 },
];

/**
 * @description Devuelve costo estimado por llamada en USD (referencial).
 * @param {string} model
 * @returns {number}
 */
export function estimatedCostUsd(model) {
  const found = ALLOWED_MODELS.find((m) => m.value === model);
  return found ? found.costPerCallUsd : 0;
}

/**
 * @description Keys reservadas para plantillas seed — no pueden usarse en custom.
 */
export const SEEDED_TEMPLATE_KEYS = [
  "post_surgery",
  "mental_health_weekly",
  "adherence",
  "oncology_post_chemo",
];

export default healthConversationTemplateService;
